import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, X } from "lucide-react";

// ID-064: add a colour variation to an EXISTING mosaic collection (reuses POST /admin/catalog/designs/{id}/combinations)
export const AddVariationDialog = ({ design, colors, open, onOpenChange, onCreated, initialColors, fullNames }) => {
  const [picked, setPicked] = useState([]);
  const [shares, setShares] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open && initialColors?.length) { setPicked(initialColors); } }, [open, initialColors]);
  const lo = design?.min_colors || 1;
  const hi = design?.max_colors || 8;
  const total = picked.reduce((s, c) => s + (Number(shares[c]) || 0), 0);
  const anyShare = picked.some((c) => shares[c] !== undefined && shares[c] !== "");
  const valid = picked.length >= lo && picked.length <= hi && (!anyShare || Math.abs(total - 100) < 0.01);
  const available = useMemo(() => colors.filter((c) => !picked.includes(c.color_code)), [colors, picked]);

  const reset = () => { setPicked([]); setShares({}); };
  const submit = async () => {
    setBusy(true);
    try {
      const body = { combinations: [{ colors: picked, ...(anyShare ? { shares: picked.map((c) => Number(shares[c]) || 0) } : {}) }] };
      const { data } = await api.post(`/admin/catalog/designs/${design.design_id}/combinations`, body);
      toast.success(`${data.skus.join(", ")} added to ${design.name} (inactive until you publish it)`);
      reset(); onOpenChange(false); onCreated?.(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-border bg-white sm:max-w-lg" data-testid="add-variation-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium text-charcoal">Add variation to {design?.name}</DialogTitle>
          <DialogDescription className="text-sm text-warmgrey">
            Pick {lo === hi ? `exactly ${lo}` : `${lo}–${hi}`} master colors. The next permanent SKU is assigned automatically; the variation starts inactive.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2" data-testid="variation-picked">
            {picked.map((c) => {
              const col = colors.find((x) => x.color_code === c) || {};
              return (
                <div key={c} className="flex items-center gap-2 rounded-md border border-border bg-sand/40 px-3 py-2" data-testid={`variation-color-${c}`}>
                  <span className="font-mono text-xs text-terracotta">{c}</span>
                  <span className="min-w-0 flex-1 truncate text-sm text-charcoal">{fullNames ? [col.moroccan_name, col.us_color_name].filter(Boolean).join(" · ") : col.us_color_name || col.moroccan_name}</span>
                  <Input type="number" min="0" max="100" placeholder="%" value={shares[c] ?? ""} onChange={(e) => setShares((s) => ({ ...s, [c]: e.target.value }))}
                    className="h-8 w-20 bg-white" data-testid={`variation-share-${c}`} />
                  <button type="button" onClick={() => setPicked((p) => p.filter((x) => x !== c))} aria-label="Remove color" data-testid={`variation-remove-${c}`}>
                    <X className="h-4 w-4 text-warmgrey hover:text-charcoal" />
                  </button>
                </div>
              );
            })}
          </div>
          {picked.length < hi && (
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Add a master color</Label>
              <div className="mt-2 flex max-h-44 flex-wrap gap-1.5 overflow-y-auto" data-testid="variation-color-options">
                {available.map((c) => (
                  <button key={c.color_code} type="button" onClick={() => setPicked((p) => [...p, c.color_code])} data-testid={`variation-option-${c.color_code}`}
                    className="rounded-full border border-border bg-white px-2.5 py-1 text-xs text-charcoal transition-colors hover:border-terracotta hover:bg-sand">
                    <span className="font-mono text-terracotta">{c.color_code}</span> {fullNames ? [c.moroccan_name, c.us_color_name].filter(Boolean).map((n) => `· ${n}`).join(" ") : c.us_color_name || c.moroccan_name}
                  </button>
                ))}
              </div>
            </div>
          )}
          <p className="text-xs text-warmgrey" data-testid="variation-rule-hint">
            {picked.length} selected{anyShare ? ` · shares total ${total}%` : " · percentages optional (must total 100% if given)"}
          </p>
          <Button onClick={submit} disabled={busy || !valid} data-testid="add-variation-submit" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create variation"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
